import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Activity, ImageIcon, Upload, BookOpen, Flag, Bell, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const KIND_META = {
  content: { icon: ImageIcon, tint: "from-orange-500/30 to-red-500/10 border-orange-500/30 text-orange-700", label: "POST" },
  library: { icon: Upload, tint: "from-blue-500/30 to-blue-500/10 border-blue-500/30 text-blue-700", label: "UPLOAD" },
  strategy: { icon: BookOpen, tint: "from-violet-500/30 to-violet-500/10 border-violet-500/30 text-violet-700", label: "STRATEGY" },
  request: { icon: Flag, tint: "from-yellow-500/30 to-yellow-500/10 border-yellow-500/30 text-yellow-700", label: "REQUEST" },
  notification: { icon: Bell, tint: "from-emerald-500/30 to-emerald-500/10 border-emerald-500/30 text-emerald-700", label: "NOTICE" },
};

const KINDS = [
  { key: "all", label: "All" },
  { key: "content", label: "Posts" },
  { key: "library", label: "Uploads" },
  { key: "strategy", label: "Strategy" },
  { key: "request", label: "Requests" },
  { key: "notification", label: "Notices" },
];

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function groupByDay(events) {
  const groups = {};
  events.forEach(e => {
    const day = (e.when || "").slice(0, 10);
    (groups[day] = groups[day] || []).push(e);
  });
  return Object.entries(groups);
}

export default function ClientTimeline() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}/timeline`);
        setData(data);
      } catch (e) { setError(formatApiError(e.response?.data?.detail) || "Could not load the timeline."); }
    })();
  }, [id]);

  if (error) return (
    <div className="p-8 space-y-3" data-testid="timeline-error">
      <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3"/> Back to Client</Link>
      <div className="panel p-6 text-sm text-rose-700">{error}</div>
    </div>
  );
  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;

  const filtered = data.events.filter(e => {
    const okKind = kind === "all" || e.kind === kind;
    const okQ = !q || `${e.title} ${e.detail}`.toLowerCase().includes(q.toLowerCase());
    return okKind && okQ;
  });
  const groups = groupByDay(filtered);
  const counts = Object.fromEntries(KINDS.map(k => [k.key, k.key === "all" ? data.events.length : data.events.filter(e => e.kind === k.key).length]));

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1200px]">
      <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3"/> Back to Client</Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">History</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">{data.client.client_name}</h1>
          <p className="text-neutral-600 text-sm mt-1">{data.total} events in this account's story</p>
        </div>
        <Activity className="w-8 h-8 text-orange-400/60"/>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search timeline..." className="pl-9 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="timeline-search"/>
        </div>
        <div className="flex gap-1 flex-wrap">
          {KINDS.map(k => (
            <button key={k.key} onClick={()=>setKind(k.key)} data-testid={`timeline-filter-${k.key}`} className={`px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-wider border transition-colors ${kind === k.key ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {k.label} <span className="text-neutral-600 ml-1">{counts[k.key]}</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="panel p-12 text-center text-neutral-500 text-sm">No events match this view.</div>
      ) : (
        <div className="space-y-6">
          {groups.map(([day, items]) => (
            <div key={day} data-testid={`timeline-day-${day}`}>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-2 pl-1">
                {day ? new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" }) : "Undated"}
              </div>
              <div className="relative pl-6">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E4E7EC]"/>
                {items.map(e => {
                  const meta = KIND_META[e.kind] || KIND_META.content;
                  const Icon = meta.icon;
                  return (
                    <div key={`${e.kind}-${e.ref_id}-${e.when}`} className="relative mb-3" data-testid={`timeline-event-${e.kind}-${e.ref_id}`}>
                      <div className={`absolute -left-6 top-1 w-3 h-3 rounded-full bg-gradient-to-br ${meta.tint} border-2 border-[#FFFFFF]`}/>
                      <div className="panel p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${meta.tint}`}>{meta.label}</span>
                          <span className="text-[10px] font-mono text-neutral-500">{fmt(e.when)}</span>
                        </div>
                        <div className="mt-1 flex items-start gap-2">
                          <Icon className="w-3.5 h-3.5 text-neutral-600 mt-0.5 shrink-0"/>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{e.title}</div>
                            {e.detail && <div className="text-[11px] font-mono text-neutral-500 mt-0.5 truncate">{e.detail}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
